import * as React from 'react'
import { useCallback, useEffect } from 'react'
import { useSelector } from 'react-redux'

import { ENTITY_TYPES } from '@sales-center/common'
import { getOpportunityNotes } from '@user/store/opportunityNotes/api'
import { NoteList } from './NoteList'
import {
  selectAllOpportunityNotes,
  selectOpportunityNoteSlice,
  setPage,
} from '@user/store/opportunityNotes/slice'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useGetOpportunityNotesOnMount } from '@user/store/opportunityNotes/hooks/useGetOpportunityNotesOnMount'

import Alert from '@mui/material/Alert'
import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import { useUpdateNoteList } from '@user/components/Notes/EntityNotes/hooks/useUpdateNoteList'
import { NoteDetailsProps } from '@user/components/Leads/LeadCard/NotesDrawer/typings'

interface Props {
  opportunityId: number
  canUpdate: boolean
  showPestPacNote: boolean
  showKnockStatusNote?: boolean
  backToNotes?: boolean
  externalLinkId: string
  sortBy: string
  detail: NoteDetailsProps
  setBackToNotes?: (value: boolean, id: number) => void
  locationsStatus: 'pending' | 'success' | 'fail'
}

export function OpportunityNotes(props: Props) {
  const {
    opportunityId,
    showPestPacNote,
    showKnockStatusNote,
    externalLinkId,
    sortBy,
    detail,
    canUpdate,
    backToNotes,
    setBackToNotes,
    locationsStatus,
  } = props
  const notesPerPage = 3
  const dispatch = useAppDispatch()

  const status = useGetOpportunityNotesOnMount({
    opportunityId,
    take: notesPerPage,
  })
  const notes = useSelector(selectAllOpportunityNotes)
  const { count, listIsStale, page, take } = useSelector(
    selectOpportunityNoteSlice,
  )
  const { onUpdateNoteLIst } = useUpdateNoteList(
    opportunityId,
    ENTITY_TYPES.Opportunity,
  )

  const getOpportunityNotesByPage = useCallback(
    async (pageNumber) => {
      dispatch(
        getOpportunityNotes({
          opportunityId,
          query: { take: notesPerPage, skip: (pageNumber - 1) * notesPerPage },
        }),
      )
      dispatch(setPage(pageNumber))
    },
    [dispatch, opportunityId],
  )

  useEffect(() => {
    if (listIsStale) {
      dispatch(
        getOpportunityNotes({
          opportunityId,
          query: { take: notesPerPage },
        }),
      )
      dispatch(setPage(1))
    }
  }, [dispatch, listIsStale, opportunityId])

  return (
    <Stack alignItems={status == 'pending' ? 'center' : 'initial'}>
      {(status == 'pending' || locationsStatus == 'pending') && (
        <CircularProgress />
      )}

      {status == 'success' && locationsStatus == 'success' && (
        <NoteList
          canUpdate={canUpdate}
          showPestPacNote={showPestPacNote}
          showKnockStatusNote={showKnockStatusNote}
          count={count}
          entityId={opportunityId}
          entityType={ENTITY_TYPES.Opportunity}
          notes={notes}
          page={page}
          onChangePage={getOpportunityNotesByPage}
          onUpdate={onUpdateNoteLIst}
          take={take}
          externalLinkId={externalLinkId}
          sortBy={sortBy}
          detail={detail}
          backToNotes={backToNotes}
          setBackToNotes={setBackToNotes}
        />
      )}

      {status == 'fail' && (
        <Box width="fit-content">
          <Alert severity="error">Failed to load opportunity notes.</Alert>
        </Box>
      )}
    </Stack>
  )
}
